import { FieldError } from 'react-hook-form';
import React from 'react';
import {
  KeyboardTypeOptions,
  NativeSyntheticEvent,
  TextInput,
  TextInputFocusEventData,
  View,
} from 'react-native';

import { Typography } from '@components/Typography';
import { Direction, useDirection } from '@providers/Direction';
import { StyleObj, makeStyles } from '@providers/Theme';

export type InputProps = {
  onChange?: (text: string) => void;
  onBlur?: (e: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  value: string;
  label?: string;
  placeholder: string;
  error?: FieldError;
  type?: string;
  keyboardType?: KeyboardTypeOptions;
  numberOfLines?: number;
  style?: {
    root?: StyleObj;
    input?: StyleObj;
    label?: StyleObj;
  };
  onSubmitEditing?: () => void;
};

type InputStylesProps = {
  direction: Direction;
};

const useStyles = makeStyles<InputStylesProps>((theme) => ({
  text: {
    marginBottom: 8,
  },
  field: {
    width: '100%',
    marginTop: 16,
    boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
  },
  input: (props) => ({
    borderWidth: 1,
    borderColor: theme.palette.grey[500],
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    width: '100%',
    placeholderTextColor: theme.palette.grey[700],
    textAlign: props.direction === 'ltr' ? 'left' : 'right',
    fontFamily: props.direction === 'ltr' ? 'Gilroy-Medium' : 'Cairo',
  }),
  inputError: {
    borderColor: theme.palette.error[300],
  },
  error: {
    width: '100%',
    marginTop: 8,
  },
}));

export const Input: React.FC<InputProps> = ({
  onChange,
  onBlur,
  value,
  label,
  placeholder,
  error,
  type,
  keyboardType,
  numberOfLines,
  style,
  onSubmitEditing,
}) => {
  const { direction } = useDirection();
  const styles = useStyles({ direction });

  return (
    <View style={[styles.field, style?.root]}>
      {label && (
        <Typography
          weight="semiBold"
          size={{ xs: 'sm', lg: 'md' }}
          variant="cool"
          style={[styles.text, style?.label]}>
          {label}
        </Typography>
      )}
      <TextInput
        onBlur={onBlur}
        onChangeText={onChange}
        value={value}
        placeholder={placeholder}
        style={[styles.input, error && styles.inputError, style?.input]}
        secureTextEntry={type === 'password'}
        keyboardType={keyboardType}
        multiline={numberOfLines !== undefined}
        numberOfLines={numberOfLines}
        onSubmitEditing={onSubmitEditing}
      />

      {error && (
        <Typography
          testID="input-error-text"
          weight="light"
          size={{ xs: 'sm', lg: 'md' }}
          variant="error"
          style={styles.error}>
          {error.message}
        </Typography>
      )}
    </View>
  );
};
